import { useLocation } from 'react-router-dom';
import { Alert } from '@zarya/ui';
import type { MiddlewareState } from '@zarya/ui';
import { useShell } from '@app/api/hooks/shell';
import { t } from '@app/i18n';

/**
 * Rotas das telas de **escrita** — as que mandam ordem ao executor (FR-125):
 * T5 (`/identidades`), T7/T8 (`/resources`), T10 (`/aprovacoes`), T12
 * (`/politicas`), T14 (`/access-control`), T15 (`/runtime`), T20 (`/infra`),
 * T21 (`/tenant`). Exportada para o teste percorrer a lista, e não uma cópia.
 */
export const WRITE_ROUTES = [
  '/identidades',
  '/resources',
  '/aprovacoes',
  '/politicas',
  '/access-control',
  '/runtime',
  '/infra',
  '/tenant',
] as const;

export function writeIsRoute(pathname: string): boolean {
  return WRITE_ROUTES.some((rota) => pathname === rota || pathname.startsWith(`${rota}/`));
}

/**
 * Estado do executor → prefixo da chave no catálogo.
 *
 * O mapa guarda a CHAVE, não a frase: os valores (`fora`, `nao_configurado`) vêm
 * do contrato e não se traduzem; o texto, sim.
 */
const TEXT_KEY: Record<Exclude<MiddlewareState, 'alcancavel'>, string> = {
  fora: 'out',
  nao_configurado: 'notConfigured',
};

/**
 * Aviso no topo de **toda** tela de escrita quando `getShell.middleware.estado`
 * é `fora` ou `nao_configurado` (FR-125) — antes de o usuário preencher, não
 * depois de o Salvar falhar. Em tela de leitura, e com o executor alcançável,
 * não renderiza nada. (US2, T142)
 */
export function MiddlewareDownNotice() {
  const { pathname } = useLocation();
  const shell = useShell();
  const estado = shell.data?.middleware.state;

  if (!writeIsRoute(pathname)) return null;
  if (!estado || estado === 'alcancavel') return null;

  const key = TEXT_KEY[estado];
  return (
    <div data-testid="middleware-down-notice" data-estado={estado}>
      <Alert tone="warning" role="status" title={t(`shell.middlewareDown.${key}.title`)}>
        {t(`shell.middlewareDown.${key}.body`)}
      </Alert>
    </div>
  );
}
